'use es6';

import { isArray } from 'laboratory-lib/core/helpers';
const TIMEOUT_MIN_MS = 0;
const TIMEOUT_MAX_MS = 60000; // This comes from https://git.hubteam.com/HubSpot/Laboratory/blob/master/LaboratoryBase/src/main/java/com/hubspot/laboratory/models/IdentifierType.java

const IDENTIFIER_TYPES = {
  PORTAL_ID: 'portalId',
  USER_ID: 'userId',
  PORTAL_USER: 'portalUser',
  UTK: 'utk',
  OTHER: 'other'
};
export function validateClient(client, caller) {
  if (!client || typeof client !== 'object' || typeof client.resolve !== 'function' || typeof client.logExposure !== 'function' || typeof client.logExposures !== 'function') {
    throw new Error(`laboratory-lib "${caller}" expected "client" to be available with these method(s) ["resolve", "logExposure", "logExposures"].`);
  }
}
export function validateCallback(callback, caller, example) {
  if (!callback || typeof callback !== 'function') {
    throw new Error(`laboratory-lib "${caller}" expected a callback argument. Example usage: "${example})"`);
  }
}
export function validateSearch(search, caller) {
  if (typeof search !== 'string') {
    throw new Error(`laboratory-lib "${caller}" expected "search" to be a string. Received "${typeof search}".`);
  }
}
export function validateStore(store, caller) {
  if (store && (typeof store.get !== 'function' || typeof store.set !== 'function' || typeof store.delete !== 'function')) {
    throw new Error(`laboratory-lib "${caller}" expected "store" to be a SuperStore instance implementing each of these method(s) ["get", "set", "delete"].`);
  }
}
export function validateTreatmentOrKeys(treatmentOrKeys, caller) {
  if (!treatmentOrKeys || !isArray(treatmentOrKeys)) {
    throw new Error(`laboratory-lib "${caller}" expects to be provided with an array of either treatment keys, or a treatment objects.`);
  }

  treatmentOrKeys.forEach(treatmentOrKey => {
    const keyMode = typeof treatmentOrKey === 'string';

    if (!treatmentOrKey) {
      throw new Error(`laboratory-lib "${caller}" expects all treatments or keys to be non-empty.`);
    } else if (!keyMode && (!treatmentOrKey.key || !treatmentOrKey.identifier)) {
      throw new Error(`laboratory-lib "${caller}" expects all treatments to contain the following properties. ["key", "identifier"].`);
    }
  });
}
export function validateTreatmentsSchema(treatments, caller) {
  if (treatments && typeof treatments !== 'object') {
    throw new Error(`laboratory-lib "${caller}" expected "treatments" to be a non-empty object.`);
  }

  Object.keys(treatments).forEach(k1 => {
    const treatment = treatments[k1];

    if (!treatment || typeof treatment !== 'object' || !treatment.identifier || !treatment.parameters) {
      throw new Error(`laboratory-lib "${caller}" expected treatments to be an object containing each of these properties ["identifier", "parameters"]. Treatment: "${k1}"`);
    }

    if (treatment.maxAge && (typeof treatment.maxAge !== 'number' || treatment.maxAge < 0)) {
      throw new Error(`laboratory-lib "${caller}" expected treatment maxAge to be a number greater than 0. Treatment: "${k1}"`);
    }

    if (typeof treatment.parameters !== 'object') {
      throw new Error(`laboratory-lib "${caller}" expected treatment parameters to be of the shape Object<String, Array>. Treatment: "${k1}"`);
    }

    Object.keys(treatment.parameters).forEach(k2 => {
      if (!isArray(treatment.parameters[k2]) || !treatment.parameters[k2].length) {
        throw new Error(`laboratory-lib "${caller}" expected parameters options to be a non-empty array. Treatment: "${k1}", Parameter: "${k2}"`);
      }
    });
  });
}
export function validateIdentifiersSchema(identifiers, caller) {
  if (identifiers && typeof identifiers !== 'object') {
    throw new Error(`laboratory-lib "${caller}" expected "identifiers" to be a non-empty object.`);
  }

  const validIdentifierKeys = Object.values(IDENTIFIER_TYPES);
  const validIdentifierKeysString = validIdentifierKeys.join("', '");
  Object.keys(identifiers).forEach(identifierType => {
    if (!validIdentifierKeys.includes(identifierType)) {
      throw new Error(`laboratory-lib "${caller}" expected identtifier to be one of ['${validIdentifierKeysString}']. Identifier: "${identifierType}"`);
    }
  });
}
export function validateExperimentsSchema({
  experiments,
  identifiers
}, caller) {
  if (experiments && typeof experiments !== 'object') {
    throw new Error(`laboratory-lib "${caller}" expected "experiments" to be a non-empty object.`);
  }

  const validIdentifierTypes = Object.keys(IDENTIFIER_TYPES);
  const validIdentifierTypesString = validIdentifierTypes.join("', '");
  Object.keys(experiments).forEach(experimentKey => {
    const experiment = experiments[experimentKey];

    if (!experiment || typeof experiment !== 'object' || !experiment.identifierType || !experiment.parameters) {
      throw new Error(`laboratory-lib "${caller}" expected experiment to be an object containing each of these properties ["identifierType", "parameters"]. Experiment: "${experimentKey}"`);
    }

    if (typeof experiment.identifierType !== 'string') {
      throw new Error(`laboratory-lib "${caller}" expected experiment identifierType to be a string. Experiment: "${experimentKey}"`);
    }

    if (!validIdentifierTypes.includes(experiment.identifierType)) {
      throw new Error(`laboratory-lib "${caller}" expected experiment identifierType to be one of ['${validIdentifierTypesString}']. Experiment: "${experimentKey}", Identifier Type: "${experiment.identifierType}"`);
    }

    const experimentIdentifierKey = IDENTIFIER_TYPES[experiment.identifierType];

    if (!Object.keys(identifiers).includes(experimentIdentifierKey)) {
      throw new Error(`laboratory-lib "${caller}" expected experiment identifierType of "${experimentIdentifierKey}" to be present on the "identifiers" object. Experiment: "${experimentKey}"`);
    }

    if (experiment.maxAge && (typeof experiment.maxAge !== 'number' || experiment.maxAge < 0)) {
      throw new Error(`laboratory-lib "${caller}" expected experiment maxAge to be a number greater than 0. Experiment: "${experimentKey}"`);
    }

    if (typeof experiment.parameters !== 'object') {
      throw new Error(`laboratory-lib "${caller}" expected experiment parameters to be of the shape Object<String, Array>. Experiment: "${experimentKey}"`);
    }

    Object.keys(experiment.parameters).forEach(parameterName => {
      if (!isArray(experiment.parameters[parameterName]) || !experiment.parameters[parameterName].length) {
        throw new Error(`laboratory-lib "${caller}" expected experiment parameters options to be a non-empty array. Experiment: "${experimentKey}", Parameter: "${parameterName}"`);
      }
    });
  });
}
export function validateHttp(http, caller) {
  if (!http || typeof http.post !== 'function' || typeof http.get !== 'function') {
    throw new Error(`laboratory-lib "${caller}" expected "http" to be a hub-http client implementing each of these method(s) ["get", "post"].`);
  }
}
export function validateTimeout(timeout, caller) {
  if (isNaN(timeout) || timeout < TIMEOUT_MIN_MS || timeout > TIMEOUT_MAX_MS) {
    throw new Error(`laboratory-lib "${caller}" expected "timeout" to be set between ${TIMEOUT_MIN_MS} - ${TIMEOUT_MAX_MS} milliseconds.`);
  }
}
export function validateQuickFetch(quickFetch, caller) {
  if (!quickFetch || typeof quickFetch !== 'object' || typeof quickFetch.getRequestStateByName !== 'function') {
    throw new Error(`laboratory-lib "${caller}" expected "window.quickFetch" to be available with these method(s) ["getRequestStateByName"].`);
  }
}
export function validateQuickFetchLabel(quickFetchLabel, caller) {
  if (!quickFetchLabel || typeof quickFetchLabel !== 'string') {
    throw new Error(`laboratory-lib "${caller}" expected "quickFetchLabel" to be a non-empty string.`);
  }
}